import moment from 'moment-timezone';
import { AzElRangeGA2, AzelRange } from 'common/utils/constants';
import { getUserTag, reformatBeamRankData, reformatHeatmapData, getSuggestedHeatmapRange } from 'common/utils/helpers';
import { SOURCE_TYPES } from './constants';

export const createFileName = (obj) => {
  const nameArray = [obj?.isDuplicate ? 'HMd' : 'HM'];
  const sourceType = SOURCE_TYPES[obj.source] ?? 'unknown';
  let timestamp = obj?.ts?.$date ? moment(obj.ts.$date).format('MMDDYY-HHmm') : 'unknown';

  if (!obj?.ts?.$date) {
    timestamp = obj?.ts ? moment(obj.ts).format('MMDDYY-HHmm') : 'unknown';
  }

  const polarization = obj?.configuration?.polarization?.[0] ?? 'unknown';
  const deviceId = obj?.deviceId ?? 'unknown';

  nameArray.push(sourceType, timestamp, polarization, deviceId);

  const fileName = nameArray.join('-');
  return fileName;
};

export const polarizationTranslator = (polarization) => {
  let displayName;
  if (polarization?.toLowerCase() === 'vpoldl' || polarization?.toLowerCase() === 'vpol') displayName = 'Vertical';
  if (polarization?.toLowerCase() === 'hpoldl'|| polarization?.toLowerCase() === 'hpol') displayName = 'Horizontal';

  return displayName ?? 'Horizontal';
};

export const convertJSONtoCSV = (json, userTag) => {
  const rank1Data = json['Rank 1 Data'] || {};
  const rank2Data = json['Rank 2 Data'] || {};
  const rank3Data = json['Rank 3 Data'] || {};
  const heatmap = json?.Heatmap || {};

  // Helper function to split Az/El and create rank data strings
  const createRankDataStr = (rankData) => {
    const [az, el] = (rankData['Az/El'] || '').split('/');
    return `${az},${el},${rankData['SSB Index']},${rankData['RSSI']},${rankData['SS-RSRP']},${rankData['SS-RSRQ']},${rankData['SS-SINR']}`;
  };

  // CSV header and data rows
  const csvStrArray = [
    `Date/Time,${json['Date/Time']},,,,,`,
    `Source,${json['Source']},,,,,`,
    `User,${json['User identifiable tag'] || 'Unknown'},,,,,`,
    `Device ID,${json['Device ID'] || 'N/A'},,,,,`,
    `Pol,${json['Polarization'].charAt(0) || 'N/A'},,,,,`,
    `PCI,${rank1Data['PCI'] || 'N/A'},,,,,`,
    `SSB ARFCN,${rank1Data['PCI'] === 'N/A' ? 'N/A': rank1Data['SSB ARFCN']},,,,,`,
    `SCS,N/A`,
    ``,
    `Rank Table,Az,El,SSB Index,RSSI,SS-RSRP,SS-RSRQ,SS-SINR,`,
    `Rank 1 Data,${createRankDataStr(rank1Data)}`,
    `Rank 2 Data,${createRankDataStr(rank2Data)}`,
    `Rank 3 Data,${createRankDataStr(rank3Data)}`,
    `Custom, N/A ,N/A,N/A,N/A,N/A, N/A,N/A`,
    ``,
    'Heatmap Table,Az,El,Power,,,'
  ];

  // Adding heatmap rows
  Object.entries(heatmap).forEach(([azEl, power], idx) => {
    const [az, el] = azEl.split(',');
    csvStrArray.push(`${idx + 1},${az},${el},${power},,,`);
  });

  // Convert the array of strings to a single CSV string
  return csvStrArray.join('\n');
};


export const csvDataParser = (record, version = 'ga1') => {
  let dateTime =
    moment(record?.ts?.['$date'] ? record?.ts?.['$date'] : record?.ts)?.format('MMM D YYYY HH:mm') ?? 'Unknown';
  let source = record?.source ?? 'Unknown';
  let user = record?.user?.name ? getUserTag(record.user.name) : 'Unknown';
  let polarization = polarizationTranslator(record?.configuration?.polarization ? record?.configuration?.polarization : record?.beamrank?.[0]?.polarity );
  let deviceId = `="${record?.deviceId}"`;
  let beamrank = reformatBeamRankData(record.beamrank, record.heatmap, version);

  return {
    'Date/Time': dateTime.toString(),
    Source: source.toString(),
    'User identifiable tag': user.toString(),
    Polarization: polarization.toString(),
    'Device ID': deviceId,
    'Rank 1 Data': {
      'Az/El': `${beamrank[0].az ?? 'N/A'}/${beamrank[0].el ?? 'N/A'}`,
      PCI: `${beamrank[0].pci ?? 'N/A'}`,
      RSSI: `${beamrank[0].rssi ?? 'N/A'}`,
      'SS-RSRP': `${beamrank[0].ssbRsrp ?? 'N/A'}`,
      'SS-RSRQ': `${beamrank[0].ssbRsrq ?? 'N/A'}`,
      'SS-SINR': `${beamrank[0].ssbSinr ?? 'N/A'}`,
      'SSB Index': `${beamrank[0].ssbIndex ?? 'N/A'}`,
      'SSB ARFCN': `${beamrank[0].ssbarfcn ?? 'N/A'}`
    },
    'Rank 2 Data': {
      'Az/El': `${beamrank[1].az ?? 'N/A'}/${beamrank[1].el ?? 'N/A'}`,
      PCI: `${beamrank[1].pci ?? 'N/A'}`,
      RSSI: `${beamrank[1].rssi ?? 'N/A'}`,
      'SS-RSRP': `${beamrank[1].ssbRsrp ?? 'N/A'}`,
      'SS-RSRQ': `${beamrank[1].ssbRsrq ?? 'N/A'}`,
      'SS-SINR': `${beamrank[1].ssbSinr ?? 'N/A'}`,
      'SSB Index': `${beamrank[1].ssbIndex ?? 'N/A'}`,
      'SSB ARFCN': `${beamrank[1].ssbarfcn ?? 'N/A'}`
    },
    'Rank 3 Data': {
      'Az/El': `${beamrank[2].az ?? 'N/A'}/${beamrank[2].el ?? 'N/A'}`,
      PCI: `${beamrank[2].pci ?? 'N/A'}`,
      RSSI: `${beamrank[2].rssi ?? 'N/A'}`,
      'SS-RSRP': `${beamrank[2].ssbRsrp ?? 'N/A'}`,
      'SS-RSRQ': `${beamrank[2].ssbRsrq ?? 'N/A'}`,
      'SS-SINR': `${beamrank[2].ssbSinr ?? 'N/A'}`,
      'SSB Index': `${beamrank[2].ssbIndex ?? 'N/A'}`,
      'SSB ARFCN': `${beamrank[2].ssbarfcn ?? 'N/A'}`
    },
    Heatmap: record?.heatmap
  };
};

export function mapRecord(record, version) {
  let { min, max } = getSuggestedHeatmapRange(version);
  let values = [];
  let beamrank = null;

  if (!record) {
    values = createEmptyHeatmapValues(min, version);

    return { values, min, max, beamrank };
  }

  if (record.heatmap) {
    values = reformatHeatmapData(record.heatmap, version);
  }

  if (record.beamrank) {
    beamrank = reformatBeamRankData(record.beamrank, record.heatmap, version);
  }

  ({ min, max } = values.reduce(
    (acc, values) => {
      acc.min = Math.min(acc.min, ...values);
      acc.max = Math.max(acc.max, ...values);

      return acc;
    },
    { min, max }
  ));

  return {
    values,
    min: Math.floor(min / 5) * 5,
    max: Math.ceil(max / 5) * 5,
    beamrank
  };
}

function createEmptyHeatmapValues(value, version) {
  const config = version === 'ga2' ? AzElRangeGA2 : AzelRange;

  return Array.from({ length: config.el.num }, () => Array.from({ length: config.az.num }, () => value));
}
